//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import ClimateParamsModel from './climate-params.model';

/**
 * @service ClimateParamsService
 * @description Service for managing climate parameter data.
 * Provides HTTP methods for retrieving climate data (temperature, humidity, etc.)
 * associated with specific ponds and dates.
 * Extends base Service class with generic type ClimateParamsModel.
 *
 * @feature 1. Fetch climate parameters by pond ID and date
 * @feature 2. Cancel ongoing HTTP requests
 *
 * @architecture
 * Extends base Service<ClimateParamsModel>. Provides access to historical or current
 * climate data stored for aquaculture ponds.
 *
 * @service-endpoint /aqu/climateparams/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ClimateParamsService extends Service<ClimateParamsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/climateparams/');
  }

  /**
   * @method getClimateParamsByPondIdDate
   * @async
   * @description Fetch climate parameters for a specific pond and date
   *
   * Makes HTTP POST request to retrieve climate data.
   *
   * @param {string} pond_id - ID of the pond
   * @param {string} date - Date string for the climate data query
   * @returns {Promise<ClimateParamsModel>} Promise resolving to ClimateParamsModel object
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getClimateParamsByPondIdDate(pond_id: string, date: string) {
    return new Promise<ClimateParamsModel>((resolve, reject) => {
      const request = this.httpClient.post<ClimateParamsModel>(`${this.baseUrl}${this.serviceUrl}pondId/date`, {
        'pondId': pond_id,
        'date': date
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
